import { atom as w, map as x, computed as b } from "nanostores";
import { copilotTools as q, copilotInstructions as O, copilotContext as z, uniqueId as E } from "./global-state.js";
function v() {
  return Math.floor(Date.now() / 1e3);
}
const I = {
  idle: ["initializing", "ending"],
  initializing: ["ready", "error"],
  ready: ["sending", "ending"],
  sending: ["receiving"],
  receiving: ["ready", "reconnecting"],
  reconnecting: ["ready", "ending"],
  ending: ["ended", "error"],
  error: ["initializing", "ending"],
  ended: []
  // Can only transition to itself (terminal state)
};
function D(i, e) {
  if (i === e)
    return i;
  if (!I[i])
    throw new Error(`Unknown status: ${i}`);
  if (I[i].includes(e))
    return e;
  throw new Error(`Invalid status transition: ${i} -> ${e}`);
}
class H {
  constructor(e) {
    this.__id = w(void 0), this.config = {
      includeGlobalContext: !0,
      includeGlobalTools: !0,
      includeGlobalInstructions: !0
    }, this.__status = w("idle"), this.__messages = w([]), this.__meta = w({}), this.__uploadFiles = w([]), this.__queuedMessages = w([]), this.__abortController = null, this.__enableWebSearch = w(!0), this.__serverSideTools = x({}), this.__sessionInstructions = x({}), this.__sessionTools = x({}), this.__sessionContext = x({}), this.__tools = b(
      [this.__serverSideTools, q, this.__sessionTools],
      (t, s, r) => {
        const n = {}, g = {
          ...this.config.includeGlobalTools === !0 ? s : {},
          ...r
        };
        for (const d of Object.values(t))
          if (d != null) {
            const h = g[d.name];
            n[d.name] = {
              ...h?.type === "server" ? h : void 0,
              ...d
            };
          }
        for (const d of Object.values(g))
          d != null && d.type === "client" && n[d.name] == null && (n[d.name] = d);
        return n;
      }
    ), this.__statefulMessages = b(
      [this.__messages, this.__tools],
      (t, s) => L(
        t,
        s,
        (r, n, c) => this.respondToToolCall(r, n, c)
      )
    ), this.__instructions = b(
      [O, this.__sessionInstructions],
      (t, s) => ({
        ...this.config.includeGlobalInstructions === !0 ? t : {},
        ...s
      })
    ), this.__context = b([z, this.__sessionContext], (t, s) => ({
      ...this.config.includeGlobalContext === !0 ? t : {},
      ...s
    })), this.__respondedToolCallIds = /* @__PURE__ */ new Set(), this.__suggestionsRefreshTrigger = w([]), e && (this.config = { ...this.config, ...e });
  }
  get id() {
    return this.__id.get();
  }
  set id(e) {
    this.__id.set(e != null ? String(e) : void 0);
  }
  /**
   * The current status of the Copilot session.
   */
  get status() {
    return this.__status.get();
  }
  /**
   * If the session is being ended.
   */
  get isEnding() {
    return this.status === "ending";
  }
  get isEnded() {
    return this.status === "ended";
  }
  /**
   * Sets the status of the Copilot session.
   */
  setStatus(e) {
    this.__status.set(D(this.status, e));
  }
  /**
   * adds an instruction just for this session
   */
  addInstruction(e) {
    const t = E();
    return this.__sessionInstructions.setKey(t, e), () => {
      this.__sessionInstructions.setKey(t, void 0);
    };
  }
  /**
   * Adds a tool just for this session
   */
  addTool(e) {
    return this.__sessionTools.setKey(e.name, e), () => {
      this.__sessionTools.setKey(e.name, void 0);
    };
  }
  /**
   * Adds a context just for this session
   */
  addContext(e) {
    const t = E();
    return this.__sessionContext.setKey(t, e), () => {
      this.__sessionContext.setKey(t, void 0);
    };
  }
  /**
   * Sets the meta data for this session
   */
  setSessionMeta(e) {
    this.__meta.set(e);
  }
  /**
   * Gets the meta data for this session
   */
  getSessionMeta() {
    return this.__meta.get();
  }
  /**
   * Sets whether the model can search the web to answer questions.
   */
  setWebSearchEnabled(e) {
    this.__enableWebSearch.set(e);
  }
  /**
   * Gets whether web search is enabled for this session.
   */
  getWebSearchEnabled() {
    return this.__enableWebSearch.get();
  }
  /**
   * Updates the meta data for a specific message by targeting message.meta.id
   */
  setMessageMeta(e, t) {
    const r = this.__messages.get().map((n) => n.role === "model" && n.type === "meta" && "meta" in n && n.meta?.id === e ? {
      ...n,
      meta: t
    } : n);
    this.__messages.set(r);
  }
  /**
   * Sends a tool_response for a human-in-the-loop tool call.
   *
   * Idempotent — calling this more than once for the same tool call ID
   * is a no-op. This protects against double-clicks and React re-renders
   * that could otherwise send duplicate responses.
   */
  respondToToolCall(e, t, s) {
    this.__respondedToolCallIds.has(e) || (this.__respondedToolCallIds.add(e), this.sendMessage([
      {
        role: "user",
        type: "tool_response",
        tool_responses: [
          {
            tool_call_id: e,
            name: t,
            response: { result: s, error: null },
            finished: !0
          }
        ],
        timestamp: v()
      }
    ]).catch(() => {
      this.__respondedToolCallIds.delete(e);
    }));
  }
  /**
   * Clears any messages that are queued to be sent after the current response completes.
   * Returns the messages that were cleared so they can be restored if needed.
   */
  clearQueuedMessages() {
    const e = this.__queuedMessages.get();
    return this.__queuedMessages.set([]), e;
  }
  /**
   * Stops the current in-flight exchange.
   *
   * Aborts the streaming HTTP request immediately (so the UI becomes responsive
   * right away) and fires a request to cancel the Temporal workflow on the
   * backend (so it stops burning LLM tokens).
   *
   * Always returns `{ messages }` — the messages that were queued while the
   * exchange was running — so the caller can restore them to the input box
   * regardless of whether the backend call succeeded. If the backend call
   * fails, `error` is also set so the caller can surface it to the user.
   *
   * Never throws. Safe to call in any state.
   */
  async stop() {
    if (this.status !== "sending" && this.status !== "receiving" && this.status !== "reconnecting")
      return { messages: [] };
    if (this.status === "reconnecting") {
      const s = this.clearQueuedMessages();
      this.setStatus("ready");
      const r = window.__hydra_copilot__?.stopExchange;
      if (r != null && this.id != null)
        try {
          await r({ id: this.id });
        } catch (n) {
          return {
            messages: s,
            error: n instanceof Error ? n : new Error(String(n))
          };
        }
      return { messages: s };
    }
    this.__abortController?.abort();
    const e = this.clearQueuedMessages(), t = window.__hydra_copilot__?.stopExchange;
    if (t != null && this.id != null)
      try {
        await t({ id: this.id });
      } catch (s) {
        return {
          messages: e,
          error: s instanceof Error ? s : new Error(String(s))
        };
      }
    return { messages: e };
  }
  /**
   * Forces a refresh of suggestions for this session.
   * This will trigger any active suggestion hooks to regenerate their suggestions.
   * @param instruction - Custom instructions to include in the next suggestions fetch.
   * These instructions will be used once and then cleared.
   */
  refreshSuggestions(e) {
    this.__suggestionsRefreshTrigger.set([{ instruction: e }]);
  }
  /**
   * Initializes a new session, either from an existing session id,
   * or by creating a new one
   */
  async initialize(e) {
    if (this.status !== "idle" && this.status !== "error")
      return this.id;
    this.setStatus("initializing");
    try {
      const t = window.__hydra_copilot__?.startSession;
      if (t == null)
        throw new Error("Copilot session not configured. Please set the Copilot configuration first.");
      if (e == null) {
        const s = await t();
        if (s.id == null)
          throw new Error("Failed to start session, no session ID returned");
        this.id = s.id, this.__serverSideTools.set(U(s.tools ?? []));
      } else {
        this.id = e;
        const s = await this.loadSession();
        this.__messages.set(s.messages ?? []), this.__meta.set(s.meta ?? {}), this.__serverSideTools.set(U(s.tools ?? []));
      }
      return this.setStatus("ready"), this.id;
    } catch (t) {
      throw this.setStatus("error"), t;
    }
  }
  /**
   * Ends the current session, clearing all messages and state.
   */
  async end() {
    this.setStatus("ending");
    try {
      const e = window.__hydra_copilot__?.endSession;
      if (e == null)
        throw new Error("Copilot session not configured. Please set the Copilot configuration first.");
      this.id != null && await e(this.id), this.__messages.set([]), this.__queuedMessages.set([]), this.__respondedToolCallIds.clear(), this.id = void 0, this.__sessionInstructions.set({}), this.__sessionTools.set({}), this.__sessionContext.set({}), this.__serverSideTools.set({}), this.__meta.set({}), this.setStatus("ended");
    } catch (e) {
      throw this.setStatus("error"), e;
    }
  }
  /**
   * Returns pending approval tool calls from the session's messages.
   * A pending approval is a tool call with `needs_approval === true` that
   * doesn't yet have a corresponding tool_response message.
   */
  getPendingApprovals() {
    const e = this.__messages.get(), t = /* @__PURE__ */ new Set();
    for (const r of e)
      if (r.role === "user" && r.type === "tool_response")
        for (const n of r.tool_responses)
          t.add(n.tool_call_id);
    const s = [];
    for (const r of e)
      if (r.role === "model" && r.type === "tool_call")
        for (const n of r.tool_calls)
          n.needs_approval === !0 && !t.has(n.id) && s.push({ id: n.id, name: n.name });
    return s;
  }
  async sendMessage(e) {
    let t = typeof e == "string" ? [{ role: "user", type: "text", text: e, timestamp: v() }] : [...e];
    const s = window.__hydra_copilot__?.sendStreamingMessage;
    if (s == null)
      throw new Error("Copilot session not configured. Please set the Copilot configuration first.");
    if ((this.status === "idle" || this.status === "error") && await this.initialize(), this.status === "initializing" && await this.waitUntilReady(), this.status !== "ready") {
      if (this.status === "sending" || this.status === "receiving" || this.status === "reconnecting") {
        this.__queuedMessages.set([...this.__queuedMessages.get(), ...t]);
        return;
      }
      throw new Error(`Cannot send message while session is in "${this.status}" state.`);
    }
    if (!t.some((d) => d.type === "tool_response")) {
      const d = this.getPendingApprovals();
      d.length > 0 && (t = [k(d), ...t]);
    }
    let n = null, c = 0, g = !1;
    try {
      this.setStatus("sending"), this.__abortController = new AbortController();
      let d = t.map((h) => ({
        ...h,
        timestamp: h.timestamp || v()
      }));
      for (; d.length > 0; ) {
        this.__messages.set([...this.__messages.get(), ...d]);
        const h = [], m = (l) => {
          this.setStatus("receiving"), c += 1, l.type === "meta" && (g = !0);
          let p = l;
          if (l.role === "model" && l.type === "tool_call") {
            const a = l.tool_calls.map((u) => {
              if (u.is_client_tool === !0 && !u.needs_approval) {
                const _ = this.__tools.get()[u.name];
                if (j(_?.needsApproval, u.args))
                  return { ...u, needs_approval: !0 };
              }
              return u;
            });
            p = { ...l, tool_calls: a };
          }
          if (this.__messages.set([...this.__messages.get(), p]), p.role === "model" && p.type === "tool_call")
            for (const a of p.tool_calls) {
              if (a.needs_approval || a.is_client_tool !== !0)
                continue;
              const u = this.__tools.get()[a.name];
              u?.humanInTheLoop !== !0 && h.push(
                (async () => {
                  try {
                    if (u?.type === "server")
                      throw new Error(
                        `Tool ${a.name} is a server-side tool and cannot be called on the client.`
                      );
                    if (u?.handler == null)
                      throw new Error(`Tool ${a.name} does not have a handler defined.`);
                    const _ = await u.handler({ args: a.args, session: this, toolCallId: a.id }) ?? null;
                    return {
                      tool_call_id: a.id,
                      name: a.name,
                      response: { result: _, error: null },
                      finished: !0
                    };
                  } catch (_) {
                    return {
                      tool_call_id: a.id,
                      name: a.name,
                      response: {
                        result: null,
                        error: `Error calling tool ${a.name}: ${_ instanceof Error ? _.message : String(_)}`
                      },
                      finished: !0
                    };
                  }
                })()
              );
            }
          if (l.role === "user" && l.type === "tool_response")
            for (const a of l.tool_responses) {
              const u = this.__tools.get()[a.name], _ = Q(
                [...this.__messages.get(), ...d],
                a.tool_call_id
              );
              u?.type === "server" && u?.handler != null && a.finished === !0 && u.handler({
                args: _?.args || {},
                session: this,
                response: a.response,
                toolCallId: a.tool_call_id,
                wasApproved: a.was_approved === !0
              });
            }
        }, M = {
          id: this.id,
          messages: d,
          tools: N(this.__tools.get()),
          instructions: G(this.__instructions.get()),
          context: K(this.__context.get()),
          onMessage: m,
          signal: this.__abortController?.signal,
          enable_web_search: this.getWebSearchEnabled()
        };
        if (await s(M), h.length === 0) {
          d = [];
          continue;
        }
        d = [
          {
            role: "user",
            type: "tool_response",
            tool_responses: await Promise.all(h),
            timestamp: v()
          }
        ];
      }
      if (c === 0)
        throw new Error("No messages received from the server. Please check your connection.");
      if (c > 0 && !g) {
        this.setStatus("reconnecting"), this.recoverInterruptedStream().catch(() => {
          this.status === "reconnecting" && this.setStatus("ready");
        });
        return;
      }
      this.setStatus("ready"), n = A(this.__queuedMessages);
    } catch (d) {
      if (this.setStatus("receiving"), this.setStatus("ready"), W(d))
        return;
      throw d;
    } finally {
      this.__abortController = null;
    }
    n != null && n.length > 0 && this.sendMessage(n).catch(() => {
    });
  }
  /**
   * Attempts to recover from an interrupted stream by reloading the session
   * from the backend. The backend continues the LLM call without streaming
   * when the connection drops, so the full response is eventually persisted.
   *
   * Retries with increasing delays to give the workflow time to complete.
   * On success, replaces local messages with the server state and transitions
   * to 'ready'. On failure, appends an error message and transitions to 'ready'.
   */
  async recoverInterruptedStream() {
    if (this.id == null || window.__hydra_copilot__?.getSession == null) {
      this.setStatus("ready");
      return;
    }
    const e = [2e3, 3e3, 5e3, 5e3, 1e4];
    for (const t of e) {
      if (await new Promise((n) => setTimeout(n, t)), this.status !== "reconnecting")
        return;
      let s;
      try {
        const n = await this.loadSession();
        s = { messages: n.messages ?? [], meta: n.meta ?? {} };
      } catch {
        continue;
      }
      if (s.messages[s.messages.length - 1]?.type === "meta") {
        if (this.status !== "reconnecting")
          return;
        this.__messages.set(s.messages), this.__meta.set(s.meta), this.setStatus("ready"), this.drainAndSendQueuedMessages();
        return;
      }
    }
    this.status === "reconnecting" && (this.__messages.set([
      ...this.__messages.get(),
      {
        role: "model",
        type: "error",
        error: "The connection was interrupted. Please try sending your message again.",
        timestamp: v()
      }
    ]), this.setStatus("ready"));
  }
  /**
   * Drains the message queue and sends the combined messages.
   * Combines text messages into a single message to avoid flooding the LLM.
   */
  drainAndSendQueuedMessages() {
    const e = A(this.__queuedMessages);
    e.length !== 0 && this.sendMessage(e).catch(() => {
    });
  }
  /**
   * Loads the session data (messages and meta)
   */
  async loadSession() {
    const e = window.__hydra_copilot__?.getSession;
    if (e == null)
      throw new Error("Copilot session not configured. Please set the Copilot configuration first.");
    if (this.id == null)
      throw new Error("Session not started");
    return await e(this.id);
  }
  /**
   * Waits for the session to be ready
   */
  async waitUntilReady() {
    if (this.status !== "ready") {
      if (this.status === "idle") {
        await this.initialize();
        return;
      }
      if (this.status === "initializing")
        return new Promise((e, t) => {
          let s = null, r = null;
          const n = () => {
            s !== null && (clearTimeout(s), s = null), r !== null && (r(), r = null);
          };
          r = this.__status.subscribe((c) => {
            c === "ready" ? (n(), e()) : c === "error" ? (n(), t(new Error("Session initialization failed"))) : c !== "initializing" && (n(), t(new Error(`Unexpected session status: ${c}`)));
          }), s = setTimeout(() => {
            n(), t(new Error("Session initialization timeout - exceeded 30 seconds"));
          }, 3e4);
        });
      throw this.status === "error" ? new Error("Session is in error state") : new Error(`Cannot wait for session in "${this.status}" state`);
    }
  }
  /**
   * Gets upload signature for file upload
   * Note: Session readiness is now checked at the upload level (uploadCopilotFile)
   */
  async getUploadSignature(e) {
    if (!this.id)
      throw new Error("Session ID not available - ensure session is ready before calling getUploadSignature");
    const t = window.__hydra_copilot__;
    if (!t?.getUploadSignature)
      throw new Error("File upload not configured. Please provide getUploadSignature in setCopilotConfig.");
    return await t.getUploadSignature({
      id: this.id,
      size: e.size,
      mime_type: e.mime_type
    });
  }
  /**
   * Gets download URL for a previously uploaded file
   */
  async getFileDownloadUrl(e) {
    if (!this.id)
      throw new Error("Session ID not available - ensure session is ready before calling getFileDownloadUrl");
    const t = window.__hydra_copilot__;
    if (!t?.getFileDownloadUrl)
      throw new Error("File download not configured. Please provide getFileDownloadUrl in setCopilotConfig.");
    return (await t.getFileDownloadUrl({
      id: this.id,
      filename: e
    })).url;
  }
  /**
   * Add files to the upload queue
   */
  addUploadFiles(e) {
    const t = this.__uploadFiles.get();
    this.__uploadFiles.set([...t, ...e]);
  }
  /**
   * Update a specific upload file
   */
  updateUploadFile(e, t) {
    const r = this.__uploadFiles.get().map((n) => n.id === e ? { ...n, ...t } : n);
    this.__uploadFiles.set(r);
  }
  /**
   * Remove upload file
   */
  removeUploadFile(e) {
    const s = this.__uploadFiles.get().filter((r) => r.id !== e);
    this.__uploadFiles.set(s);
  }
  /**
   * Clear all upload files
   */
  clearUploadFiles() {
    this.__uploadFiles.set([]);
  }
}
function F(i) {
  return typeof i == "string" ? i : typeof i == "number" || typeof i == "boolean" ? String(i) : Array.isArray(i) || typeof i == "object" ? JSON.stringify(i) : String(i);
}
function $(i) {
  const e = /* @__PURE__ */ new Map(), t = /* @__PURE__ */ new Set(), s = i.filter((r) => r.was_approved === !0 && r.response != null);
  for (const r of s) {
    const n = i.findIndex(
      (c, g) => c !== r && !t.has(g) && c.name === r.name && c.needs_approval === !0 && JSON.stringify(c.args) === JSON.stringify(r.args)
    );
    n !== -1 && (e.set(n, r.response), t.add(n));
  }
  return e;
}
function L(i, e, t) {
  const s = [];
  let r = [], n = [], c = [];
  const g = {}, d = i.filter(
    (o) => {
      if (o.role === "user" && o.type === "tool_response") {
        for (const l of o.tool_responses)
          l.tool_call_id != null && (g[l.tool_call_id] == null && (g[l.tool_call_id] = []), g[l.tool_call_id].push(l));
        return !1;
      }
      return o.type === "text" || o.type === "content" || o.type === "tool_call" || o.type === "meta" || o.type === "error" || o.type === "compaction_start" || o.type === "compaction_complete";
    }
  );
  function h() {
    const o = r.filter((f) => f.type === "text"), l = r.find((f) => f.type === "meta" && "meta" in f);
    if (r = [], o.length === 0)
      return;
    const p = o.map((f) => f.text).join(""), a = l?.meta ?? {}, u = o[0]?.thought;
    let _;
    if (u === !0) {
      const f = s[s.length - 1];
      f != null && f.timestamp != null ? _ = Math.max(0, o[o.length - 1].timestamp - f.timestamp) : _ = 0;
    }
    s.push({
      role: "model",
      type: "text",
      text: p,
      timestamp: o[0].timestamp,
      meta: a,
      ...u === !0 && { thought: u },
      ..._ != null && { duration: _ }
    }), r = [];
  }
  function m() {
    if (n.length === 0)
      return;
    const o = n.find((a) => a.type === "text"), l = n.filter((a) => a.type === "content"), p = n[0];
    s.push({
      role: "user",
      type: "text",
      text: o?.text ?? "",
      attachments: l.map((a) => ({
        content: a.content,
        mime_type: a.mime_type,
        display_name: a.display_name
      })),
      timestamp: p.timestamp ?? 0
    }), n = [];
  }
  function M(o) {
    const l = g[o.id], p = e[o.name], u = o.needs_approval === !0 || o.is_client_tool === !0 && j(p?.needsApproval, o.args) ? { ...o, needs_approval: !0 } : o;
    if (l == null || l.length === 0) {
      const y = o.is_client_tool === !0 && p?.humanInTheLoop === !0;
      return {
        ...u,
        status: "executing",
        ...y && {
          respond: (P) => t(o.id, o.name, P)
        }
      };
    }
    const _ = l.find((y) => y.response.error != null && y.response.error !== ""), f = l.some((y) => y.finished), C = l.some((y) => y.was_approved === !0);
    if (_ != null)
      return {
        ...u,
        status: "error",
        response: _.response,
        ...C && { was_approved: !0 }
      };
    const R = p?.type === "server" && p?.isStreaming === !0;
    return f && l.length === 1 && !R ? {
      ...u,
      status: "success",
      response: l[0].response,
      ...C && { was_approved: !0 }
    } : {
      ...u,
      status: f ? "success" : "executing",
      response: {
        result: l.map((y) => y.response.result),
        error: null
      },
      ...C && { was_approved: !0 }
    };
  }
  function S() {
    if (c.length === 0)
      return;
    const o = c.flatMap((a) => a.tool_calls.map(M)), l = $(o), p = o.map((a, u) => l.has(u) ? { ...a, response: l.get(u) } : a).filter((a) => a.was_approved !== !0);
    p.length > 0 && s.push({
      role: "model",
      type: "tool_call",
      tool_calls: p,
      timestamp: c[0].timestamp
    }), c = [];
  }
  for (const o of d)
    if (o.role === "model" && o.type === "text") {
      m(), S();
      const l = r.some((u) => u.type === "text"), p = o.thought === !0, a = r.some(
        (u) => u.type === "text" && "thought" in u && u.thought === !0
      );
      l && (p && !a || !p && a) && h(), r.push(o);
    } else if (o.role === "user" && (o.type === "text" || o.type === "content"))
      h(), S(), o.type === "text" && n.some((l) => l.type === "text") && m(), n.push(o);
    else if (o.role === "model" && o.type === "meta")
      m(), r.push(o), h();
    else if (o.role === "model" && o.type === "tool_call")
      h(), m(), c.push(o);
    else if (o.role === "model" && o.type === "compaction_start")
      h(), m(), S(), s.push({
        role: "model",
        type: "compaction",
        status: "compacting",
        timestamp: o.timestamp
      });
    else if (o.role === "model" && o.type === "compaction_complete") {
      h(), m(), S();
      const l = s.findIndex(
        (a) => a.role === "model" && a.type === "compaction" && a.status === "compacting"
      ), p = {
        role: "model",
        type: "compaction",
        status: "complete",
        summary: o.text,
        timestamp: o.timestamp
      };
      l >= 0 ? s[l] = p : s.push(p);
    } else
      o.role === "model" && o.type === "error" ? (h(), m(), S(), s.push(o)) : console.error("Unknown message type", o);
  h(), m(), S();
  for (let o = 0; o < s.length; o++) {
    const l = s[o];
    if (l.role !== "model" || l.type !== "tool_call" || !l.tool_calls.some((u) => u.status === "executing"))
      continue;
    s.slice(o + 1).some((u) => u.role === "model" && (u.type === "text" || u.type === "tool_call" || u.type === "error")) && (s[o] = {
      ...l,
      tool_calls: l.tool_calls.map((u) => u.status === "executing" ? { ...u, status: "success" } : u)
    });
  }
  return s;
}
function N(i) {
  return Object.values(i).filter((e) => e !== void 0 && e.type === "client").map((e) => ({
    name: e.name,
    description: typeof e.description == "string" ? T(e.description) : e.description,
    request_json_schema: e.requestSchema,
    json_response_schema: e.responseSchema
  }));
}
function G(i) {
  return Array.isArray(i) ? i.map((e) => T(e.instruction).trim()) : Object.values(i).filter((e) => e !== void 0).map((e) => T(e.instruction).trim());
}
function K(i) {
  return Array.isArray(i) ? i.map((e) => ({
    description: T(e.description),
    value: F(typeof e.value == "function" ? e.value() : e.value)
  })) : Object.values(i).filter((e) => e !== void 0).map((e) => ({
    description: T(e.description),
    value: F(typeof e.value == "function" ? e.value() : e.value)
  }));
}
function j(i, e) {
  if (typeof i == "function")
    try {
      return i(e);
    } catch {
      return !0;
    }
  return i === !0;
}
function T(i) {
  const e = i.split(`
`), t = e.filter((c) => c.trim().length > 0);
  if (t.length === 0)
    return i;
  const s = t.map((c) => {
    const g = c.match(/^(\s*)/);
    return g ? g[1].length : 0;
  }), r = Math.min(...s);
  return e.map((c) => c.trim().length === 0 ? c : c.slice(r)).join(`
`);
}
function Q(i, e) {
  for (const t of i)
    if (t.role === "model" && t.type === "tool_call") {
      const s = t.tool_calls.find((r) => r.id === e);
      if (s)
        return s;
    }
}
function A(i) {
  const e = i.get();
  if (e.length === 0)
    return [];
  i.set([]);
  const t = e.filter((n) => n.type === "text" && "text" in n), r = [...e.filter((n) => n.type !== "text")];
  if (t.length > 0) {
    const n = t.map((c) => "text" in c ? c.text : "").join(`

`);
    r.push({
      role: "user",
      type: "text",
      text: n,
      timestamp: v()
    });
  }
  return r;
}
function W(i) {
  return i instanceof DOMException && i.name === "AbortError" || i != null && typeof i == "object" && "name" in i && i.name === "CanceledError";
}
function k(i) {
  return {
    role: "user",
    type: "tool_response",
    tool_responses: i.map((e) => ({
      tool_call_id: e.id,
      name: e.name,
      response: { result: { status: "rejected" }, error: null },
      finished: !0
    })),
    timestamp: v()
  };
}
function U(i) {
  const e = {};
  for (const t of i)
    e[t.name] = {
      type: "server",
      name: t.name,
      description: t.description,
      requestSchema: t.request_json_schema,
      responseSchema: t.response_json_schema
    };
  return e;
}
export {
  H as CopilotSession,
  Q as findToolCallById,
  K as prepareContextForTransport,
  G as prepareInstructionsForTransport,
  N as prepareToolsForTransport,
  j as resolveNeedsApproval,
  U as serverSideToolsToCopilotTools,
  F as stringifyForCopilot,
  T as undent,
  D as updatedStatus
};
